import { indexedDBManager } from '../utils/indexedDB';
import { PriceStatusEnum } from '../enums/PriceStatusEnum';

export interface FormattedPrice {
  price: any;
  product: string;
  presentation: string;
  status?: string;
}

export const buildPresentationMap = (rawPresentations: any[]): Map<string, string> => {
  const presentationMap = new Map<string, string>();
  if (!Array.isArray(rawPresentations)) return presentationMap;

  rawPresentations.forEach((item) => {
    const presentation = item?.data || item;
    if (!presentation) return;

    const displayName =
      presentation.name ||
      presentation.presentation ||
      presentation.productName ||
      presentation.brandName;

    if (displayName) {
      if (presentation.refId) presentationMap.set(String(presentation.refId), displayName);
      if (presentation.id) presentationMap.set(String(presentation.id), displayName);
    }
  });

  return presentationMap;
};

export const resolveProductName = (
  priceRecord: any,
  presentationMap: Map<string, string>
): string => {
  const productRef = priceRecord.productRefId || priceRecord.product;
  if (productRef && presentationMap.has(String(productRef))) {
    return presentationMap.get(String(productRef))!;
  }
  return priceRecord.productName || priceRecord.product || priceRecord.productRefId || 'Product';
};

export const buildPricesIndexMap = (allPrices: any[], presentationMap: Map<string, string>) => {
  const map = new Map<string, FormattedPrice[]>();
  if (!Array.isArray(allPrices)) return map;

  allPrices.forEach((item) => {
    const priceItem = item?.data || item;
    if (!priceItem || priceItem.isActive === false) return;

    const formattedPrice: FormattedPrice = {
      price: priceItem.price,
      product: resolveProductName(priceItem, presentationMap),
      presentation: priceItem.presentation || '',
      status:
        priceItem.status ||
        (priceItem.isNotFound ? PriceStatusEnum.PRODUCTNOTFOUND : PriceStatusEnum.APPROVED),
    };

    const keys = [priceItem.inspectionRefId, priceItem.taskRefId].filter(Boolean);
    keys.forEach((key) => {
      const keyStr = String(key);
      if (!map.has(keyStr)) {
        map.set(keyStr, []);
      }
      map.get(keyStr)!.push(formattedPrice);
    });
  });

  return map;
};

export const normalizeInspectionRecord = (
  rawInspection: any,
  computedPrices: FormattedPrice[],
  defaultInspectorName: string
) => {
  let inspectorName = defaultInspectorName;

  if (rawInspection.doneBy) {
    if (typeof rawInspection.doneBy === 'string' && rawInspection.doneBy.trim() !== '') {
      inspectorName = rawInspection.doneBy;
    } else if (typeof rawInspection.doneBy === 'object' && rawInspection.doneBy.name) {
      inspectorName = rawInspection.doneBy.name;
    }
  } else if (rawInspection.doneByName) {
    inspectorName = rawInspection.doneByName;
  }

  const existingPrices = Array.isArray(rawInspection.prices) ? rawInspection.prices : [];

  return {
    ...rawInspection,
    date:
      rawInspection.date || rawInspection.reportedTime || rawInspection.createTime || Date.now(),
    doneBy: { name: inspectorName },
    prices: existingPrices.length > 0 ? existingPrices : computedPrices,
  };
};

export const enrichTaskWithInspections = (
  task: any,
  localInspectionsByTask: Map<string, any[]>,
  pricesIndexMap: Map<string, FormattedPrice[]>,
  defaultInspectorName: string
) => {
  const taskIdString = String(task.refId || task.id || '');

  const matchingLocalInspections = localInspectionsByTask.get(taskIdString) || [];
  const existingInspections = Array.isArray(task.inspections) ? task.inspections : [];
  const uniqueInspectionMap = new Map<string, any>();

  const processInspection = (inspection: any, inspectionIndex: number) => {
    const uniqueInspectionKey = inspection.refId
      ? String(inspection.refId)
      : inspection.id
        ? String(inspection.id)
        : inspection.createTime
          ? `time-${inspection.createTime}`
          : inspection.reportedTime
            ? `rep-${inspection.reportedTime}`
            : `index-${inspectionIndex}`;

    const inspectionRefId = String(inspection.refId || inspection.id || '');
    const computedPrices =
      (inspectionRefId && pricesIndexMap.get(inspectionRefId)) ||
      (taskIdString && pricesIndexMap.get(taskIdString)) ||
      [];

    uniqueInspectionMap.set(
      uniqueInspectionKey,
      normalizeInspectionRecord(inspection, computedPrices, defaultInspectorName)
    );
  };

  existingInspections.forEach((inspection: any, index: number) =>
    processInspection(inspection, index)
  );
  matchingLocalInspections.forEach((inspection: any, index: number) =>
    processInspection(inspection, index)
  );

  return {
    ...task,
    inspections: Array.from(uniqueInspectionMap.values()),
  };
};

export const fetchAndEnrichOfflineTasks = async (offlineTasks: any[]) => {
  if (!offlineTasks || offlineTasks.length === 0) return [];

  const rawInspections = await indexedDBManager.getLocalData('inspection');
  const rawPrices = await indexedDBManager.getLocalData('price');
  const rawPresentations = await indexedDBManager.getLocalData('presentation');

  const allInspections = Array.isArray(rawInspections) ? rawInspections : [];
  const allPrices = Array.isArray(rawPrices) ? rawPrices : [];
  const allPresentations = Array.isArray(rawPresentations) ? rawPresentations : [];

  const presentationMap = buildPresentationMap(allPresentations);
  const pricesIndexMap = buildPricesIndexMap(allPrices, presentationMap);
  const defaultInspectorName =
    (typeof window !== 'undefined' && localStorage.getItem('name')) || '';

  const localInspectionsByTask = new Map<string, any[]>();
  allInspections.forEach((item) => {
    const inspectionItem = item?.data || item;
    if (!inspectionItem || inspectionItem.isActive === false) return;

    const taskKeys = [inspectionItem.taskRefId, inspectionItem.taskId].filter(Boolean);
    taskKeys.forEach((key) => {
      const keyStr = String(key);
      if (!localInspectionsByTask.has(keyStr)) {
        localInspectionsByTask.set(keyStr, []);
      }
      localInspectionsByTask.get(keyStr)!.push(inspectionItem);
    });
  });

  return offlineTasks.map((task) =>
    enrichTaskWithInspections(task, localInspectionsByTask, pricesIndexMap, defaultInspectorName)
  );
};
